import {
    Flex,
    useStyles,
    Pagination,
    PaginationItem,
    Grid,
    Divider,
    useTheme,
} from '@archipro-design/aria';
import type { PaginationItemCounterProps } from '@archipro-design/aria';

import { Link, useLocation, useSearchParams } from '@remix-run/react';
import * as S from './PaginatedVideos.style';
import VideoGrid from '@modules/professional/component/video-tab/VideoGrid';
import VideoSort from '@modules/professional/component/video-tab/VideoSort';
import { useTracker } from '@archipro-website/tracker';
import type { PaginatedVideosProps } from '../../api/list-professional-videos.server';
import { PROFESSIONAL_SECTIONS_ANCHOR } from '../../util/constants';

const PaginatedVideosDesktop: React.FC<PaginatedVideosProps> = ({
    videos,
    totalCount,
    totalPage,
    currentPage,
    pageSize,
    initialSort,
    hasPreviousPage,
    hasNextPage,
}) => {
    const { css } = useStyles();
    const { siteVariables } = useTheme();

    const location = useLocation();

    const [searchParams] = useSearchParams();

    const tracker = useTracker();

    const renderPaginationLink = (page: number | null) => {
        const copyParams = new URLSearchParams(searchParams);

        if (page) {
            copyParams.set('page', page.toString());
        }

        return `${location.pathname}?${copyParams.toString()}#${PROFESSIONAL_SECTIONS_ANCHOR}`;
    };

    return (
        <div className={css(S.VideoSection)}>
            <Divider
                size={1}
                variables={{
                    dividerPadding: 0,
                    dividerPrimaryColor: siteVariables.colors.primitive.black,
                }}
            />
            <Grid columns={'100%'} className={css(S.Container)}>
                <Flex className={css(S.FilterBar)}>
                    <Flex className={css(S.DesktopFilterGroup)}>
                        <VideoSort initialSort={initialSort} />
                    </Flex>
                </Flex>
                <div className={css(S.ResultSection)}>
                    <VideoGrid videos={videos} />
                </div>
                <Flex className={css(S.PaginationWrapper)} hAlign="center">
                    {totalPage > 1 && (
                        <Pagination
                            prevButton={{}}
                            nextButton={{}}
                            renderItem={(item) => {
                                const disablePrev =
                                    item.type === 'previous' &&
                                    !hasPreviousPage;
                                const disableNext =
                                    item.type === 'next' && !hasNextPage;

                                if (disablePrev || disableNext) {
                                    return PaginationItem.create(
                                        {
                                            content: item,
                                        },
                                        { generateKey: true }
                                    );
                                }
                                return PaginationItem.create({
                                    as: Link,
                                    key: `paginationItem-${item.type}-${item.page}`,
                                    prefetch: 'intent',
                                    to: renderPaginationLink(item.page),
                                    content: item,
                                });
                            }}
                            defaultPage={currentPage}
                            count={totalPage}
                            counter={{
                                range: {
                                    from: (currentPage - 1) * pageSize + 1,
                                    to:
                                        currentPage * pageSize > totalCount
                                            ? totalCount
                                            : currentPage * pageSize,
                                },
                                total: totalCount,
                                textRenderer: (
                                    range: PaginationItemCounterProps['range'],
                                    total: PaginationItemCounterProps['total']
                                ) =>
                                    `${range.from} - ${range.to} of ${total} Videos`,
                            }}
                            totalCount={totalCount}
                            pageSize={pageSize}
                            onChange={(_, page) => {
                                if (page) {
                                    tracker.log('ProfessionalVideoPageChange', {
                                        url: new URL(window.location.href),
                                        targetTracker: 'archiproTracker',
                                        data: {
                                            ExtraData: page,
                                        },
                                    });
                                }
                            }}
                        />
                    )}
                </Flex>
            </Grid>
        </div>
    );
};

export default PaginatedVideosDesktop;
